<script>
import AkAvatar from '@components/general/AkAvatar.vue';
import Spinner from '@components/v2/general/Spinner.vue';
import ReadIndicator from '@views/threads/parts/ReadIndicator.vue';
import VideoPlayerModal from '@views/threads/parts/VideoPlayerModal.vue';
import ImageViewerModal from '@views/threads/parts/ImageViewerModal.vue';
import reservationSiteConst from '@mixins/const/reservationSiteConst';
import moment from 'moment';

export default {
  name: 'MessageBubble',
  mixins: [reservationSiteConst],
  components: {AkAvatar, Spinner, ReadIndicator, VideoPlayerModal, ImageViewerModal},
  props: {
    message: {
      type: Object,
      required: true,
    },
    isLastInCluster: {
      type: Boolean,
      default: true,
    },
    isFirstInCluster: {
      type: Boolean,
      default: true,
    },
    isLastMessage: {
      type: Boolean,
      default: false,
    },
    guests: {
      type: Array,
      default: () => [],
    },
    lastMessagesRead: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isOriginal: false,
      videoDuration: null,
      showMediaModal: false,
    };
  },
  computed: {
    isSending() {
      return this.message.sending;
    },
    isSent() {
      return this.message.sent === true;
    },
    hasError() {
      return this.message.hasError === true;
    },
    isRight() {
      return this.isSent || this.isSending;
    },
    sentMoment() {
      const m = moment.utc(this.message.sentMoment).local();
      return m.format('HH[h]mm');
    },
    messageContent() {
      return this.isOriginal
        ? this.message.originalContent
        : (this.message.translatedContent ?? this.message.originalContent);
    },
    hasOtherLanguage() {
      return this.message.translatedContent;
    },
    messageContentParts() {
      if (!this.messageContent) return [];
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      const parts = [];
      let lastIndex = 0;
      let match;
      while ((match = urlRegex.exec(this.messageContent)) !== null) {
        if (match.index > lastIndex) parts.push({text: this.messageContent.slice(lastIndex, match.index)});
        parts.push({url: match[0]});
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < this.messageContent.length) parts.push({text: this.messageContent.slice(lastIndex)});
      return parts;
    },
    hasMedia() {
      return Boolean(this.message.ficMd5 || this.message.mediaUrl);
    },
    isVideo() {
      return this.message.mediaType === 'video' || this.message.mediaType?.startsWith('video/');
    },
    mediaUrl() {
      if (this.message.mediaUrl) return this.message.mediaUrl;
      if (!this.message.ficMd5) return null;
      const pictureBase = `${import.meta.env.VUE_APP_PUBLIC_API_URL}/picture/${this.message.ficMd5}`;
      const videoBase = `${import.meta.env.VUE_APP_PUBLIC_API_URL}/video/${this.message.ficMd5}`;
      return this.isVideo ? videoBase : pictureBase;
    },
    showReadIndicator() {
      return this.isLastInCluster && this.isLastMessage && this.isSent && !this.isSending;
    },
    formattedVideoDuration() {
      if (this.videoDuration == null) return null;
      const seconds = Math.round(this.videoDuration);
      return `0:${String(seconds).padStart(2, '0')}`;
    },
  },
  methods: {
    toggleOriginal() {
      this.isOriginal = !this.isOriginal;
    },
    onVideoLoadedMetadata(e) {
      this.videoDuration = e.target.duration;
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-1" :class="{'mb-2': isLastInCluster}">
    <div
      class="flex gap-2 items-center [&:hover>#translateBtn]:opacity-100"
      :class="[isRight ? 'md:ml-[min(80px,15%)] self-end flex-row-reverse' : 'md:mr-[min(80px,15%)]']">
      <div
        class="relative text-grey-900 text-md font-medium leading-[24px] w-fit max-w-[320px] md:max-w-none bg-[#F7F9FB] flex items-center overflow-hidden"
        :class="[
          {'rounded-br-xl': isLastInCluster && !isFirstInCluster && isRight},
          {'rounded-bl-xl': isLastInCluster && !isFirstInCluster && !isRight},
          {'rounded-tr-none': !isFirstInCluster && isRight},
          {'rounded-tl-none': !isFirstInCluster && !isRight},
          {'rounded-bl-none rounded-tl-xl': isFirstInCluster && !isRight},
          {'rounded-br-none rounded-tr-xl': isFirstInCluster && isRight},
          {'rounded-r-xl': !isRight},
          {'self-end rounded-l-xl': isSent && !isSending},
          {'bg-grey-50 self-end rounded-l-xl': isSending},
          {'bg-red-50 self-end rounded-l-xl': hasError},
        ]">
        <div v-if="hasMedia" class="relative h-[200px] max-w-[280px] overflow-hidden">
          <template v-if="isVideo">
            <video
              :src="mediaUrl"
              muted
              playsinline
              class="h-full w-full object-cover cursor-pointer"
              @loadedmetadata="onVideoLoadedMetadata"
              @click="showMediaModal = true" />
            <div class="absolute inset-0 bg-black/5 flex items-center justify-center pointer-events-none">
              <div class="h-12 w-12 rounded-full bg-black/60 flex items-center justify-center">
                <svg viewBox="0 0 24 24" class="h-8 w-8 ml-0.5 fill-white"><path d="M6 4l15 8-15 8z" /></svg>
              </div>
            </div>
            <div
              v-if="formattedVideoDuration"
              class="absolute bottom-2 left-2 px-2 py-0.5 rounded-full bg-black/60 text-white text-xs font-semibold">
              {{ formattedVideoDuration }}
            </div>
          </template>
          <img
            v-else
            :src="mediaUrl"
            alt=""
            class="h-full w-full object-cover cursor-pointer"
            @click="showMediaModal = true" />
        </div>
        <p
          v-else-if="messageContent"
          class="py-3 px-4 text-grey-900 text-[15px] whitespace-pre-wrap w-full"
          :class="{'!text-grey-500': isSending}">
          <template v-for="(part, index) in messageContentParts" :key="index">
            <a
              v-if="part.url"
              :href="part.url"
              target="_blank"
              rel="noopener noreferrer"
              class="underline text-[15px] break-all">
              {{ part.url }}
            </a>
            <template v-else>{{ part.text }}</template>
          </template>
        </p>
        <p v-else-if="message.deleted" class="text-grey-300 font-medium italic py-3 px-4">Message supprimé</p>
        <div v-if="isSending && hasMedia" class="absolute inset-0 bg-white/50 flex items-center justify-center">
          <Spinner :size="24" color="#00122c" track-color="var(--color-grey-400)" :stroke-width="3" />
        </div>
      </div>

      <button
        v-if="hasOtherLanguage"
        @click="toggleOriginal"
        id="translateBtn"
        class="flex items-center justify-center opacity-0 shrink-0 text-grey-700 hover:text-grey-900 focus-visible:text-grey-900"
        :title="isOriginal ? 'Afficher le message traduit' : 'Afficher le message original'">
        <i class="ga-icon ga-languages text-lg" />
      </button>
    </div>
    <div
      v-if="!isLastInCluster && message.edited"
      class="font-medium text-grey-500 text-sm mb-1 -mt-0.5"
      :class="{'self-end': isRight}">
      Modifié
    </div>
    <div v-if="hasError" class="flex items-center gap-2" :class="{'self-end': isRight}">
      <span class="font-medium text-red-600 text-sm">Échec de l'envoi</span>
      <button
        :disabled="message.resending"
        @click="$emit('resend', message.id)"
        class="font-medium text-red-600 hover:text-red-700 focus-visible:text-red-700 text-sm underline disabled:opacity-50 disabled:pointer-events-none">
        {{ message.resending ? 'Envoi…' : 'Renvoyer' }}
      </button>
    </div>
    <div v-if="isLastInCluster" class="flex items-center gap-2" :class="{'flex-row-reverse': isRight}">
      <i v-if="isSent || isSending" class="logo-site guestadom" />
      <span
        v-else-if="!message.authorPictureUrl"
        class="flex items-center justify-center font-semibold h-[22px] w-[22px] rounded-full bg-grey-50 text-grey-500 text-sm">
        {{ message.authorDisplay[0] }}
      </span>
      <img
        v-else
        :src="message.authorPictureUrl"
        :alt="message.authorDisplay[0]"
        class="h-[22px] w-[22px] rounded-full" />
      <span class="font-medium text-grey-500 text-sm" :class="{italic: isSending}">
        {{ message.authorDisplay }},
        {{
          isSending ? 'Envoi en cours' : sentMoment + ' via ' + displayReservationSite(message.platform.toLowerCase())
        }}
        <template v-if="message.edited">· Modifié</template>
      </span>
      <read-indicator
        v-if="showReadIndicator"
        :message-id="message.id"
        :guests="guests"
        :last-messages-read="lastMessagesRead"
        :sent-date="message.sentMoment" />
    </div>
    <Teleport to="body">
      <VideoPlayerModal
        v-if="hasMedia && isVideo && showMediaModal"
        :src="mediaUrl"
        :author-display="message.authorDisplay"
        :author-picture-url="message.authorPictureUrl"
        @close="showMediaModal = false" />
      <ImageViewerModal
        v-if="hasMedia && !isVideo && showMediaModal"
        :src="mediaUrl"
        :author-display="message.authorDisplay"
        :author-picture-url="message.authorPictureUrl"
        @close="showMediaModal = false" />
    </Teleport>
  </div>
</template>

<style scoped></style>
